import { useEffect, useState } from 'react';
import { getBureauAlarmsByCategory } from '../services/bureauManagement/bureauManagement.service';
import { alarmAcknowledgedEvent } from '../services/events/alarmEvents';
import { useBureauSignalRContext } from '../contexts/BureauSignalRContext';

interface DashboardCounts {
  activeAck: number;
  activeUnack: number;
  inactiveUnack: number;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Failed to load dashboard counts';
}

function getTotalCount(response: { success?: boolean; data?: { totalCount?: number | null } | null } | null | undefined): number {
  if (!response?.success) {
    return 0;
  }
  return response.data?.totalCount ?? 0;
}

/**
 * Shared hook that fetches all dashboard alarm counts in a single batch.
 * All dashboard cards should use this hook instead of making separate requests.
 * This reduces network calls from 3 separate requests to 1 parallel batch.
 */
export function useDashboardCounts() {
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { lastNotification } = useBureauSignalRContext();

  const fetchCounts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all category counts in parallel
      const [activeAckRes, activeUnackRes, inactiveUnackRes] = await Promise.all([
        getBureauAlarmsByCategory('active-ack', 1, 1),
        getBureauAlarmsByCategory('active-not-ack', 1, 1),
        getBureauAlarmsByCategory('not-active-not-ack', 1, 1)
      ]);

      const activeAck = getTotalCount(activeAckRes);
      const activeUnack = getTotalCount(activeUnackRes);
      const inactiveUnack = getTotalCount(inactiveUnackRes);

      setCounts({
        activeAck,
        activeUnack,
        inactiveUnack
      });
    } catch (caughtError: unknown) {
      setError(getErrorMessage(caughtError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const doFetch = async () => {
      await fetchCounts();
    };

    void doFetch();

    // Refresh counts when alarms are acknowledged
    const unsubscribe = alarmAcknowledgedEvent.subscribe((payload) => {
      if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
        return;
      }
      if (mounted) {
        void doFetch();
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [lastNotification]);

  return { counts, loading, error, refresh: fetchCounts };
}
